import { TaskStatus } from "client/openapi";
import { aboutRoles, Facets, isTaskKind } from "./kinds";
import { formName, nameOf, ReferenceData, roleOf } from "./reference";
import type { AssignChoice, AssignTo, PersonKey, Trigger } from "./types";

/*
 * Who the Assign to choices mean for one event, as the pickers and the
 * editor's estimate show them: on a sample event from the organization's
 * own people.
 */

/** Who and what an event is about. */
export interface RunContext {
  about: PersonKey | null;
  /** The student in context (the one it is about, or a parent's first student). */
  student: PersonKey | null;
  /** The student's parents (or the parent it is about). */
  parents: PersonKey[];
  /** The meeting's tutor, for a meeting event. */
  meetingTutor: PersonKey | null;
  /** Who finished or changed the task, or filled out the form. */
  eventPerson: PersonKey | null;
  /** The task, for a task event. */
  task: {
    id: string;
    name: string;
    status: TaskStatus;
    assignees: PersonKey[];
    about: PersonKey[];
  } | null;
  formId: string | null;
  column: string | null;
}

export function emptyContext(): RunContext {
  return {
    about: null,
    student: null,
    parents: [],
    meetingTutor: null,
    eventPerson: null,
    task: null,
    formId: null,
    column: null,
  };
}

/** A person's family in context: their student and that student's parents. */
export function familyOf(ref: ReferenceData, about: PersonKey | null) {
  if (!about) return { student: null, parents: [] as PersonKey[] };
  const role = roleOf(about);
  if (role === "student")
    return { student: about, parents: ref.parentsOf[about] ?? [] };
  if (role === "parent")
    return {
      student: ref.studentsOfParent[about]?.[0] ?? null,
      parents: [about],
    };
  return { student: null, parents: [] as PersonKey[] };
}

/**
 * Their tutors: the meeting's tutor for a meeting event, else the tutors of
 * the student (of every student of a parent), only those who teach
 * `subject` when one is picked.
 */
export function tutorsInContext(
  ref: ReferenceData,
  ctx: RunContext,
  subject: number | null
) {
  let tutors: PersonKey[] = ctx.meetingTutor ? [ctx.meetingTutor] : [];
  if (!tutors.length) {
    const students =
      ctx.about && roleOf(ctx.about) === "parent"
        ? ref.studentsOfParent[ctx.about] ?? []
        : ctx.student
        ? [ctx.student]
        : [];
    tutors = Array.from(
      new Set(students.flatMap((s) => ref.tutorsOf[s] ?? []))
    );
  }
  return subject === null
    ? tutors
    : tutors.filter((t) => ref.subjectsOfTutor[t]?.includes(subject));
}

/** The people one Assign to choice means for this event. */
export function resolveChoice(
  ref: ReferenceData,
  ctx: RunContext,
  choice: AssignChoice,
  assign: AssignTo,
  facets: Facets
): PersonKey[] {
  const known = (keys: (PersonKey | null | undefined)[]) =>
    keys.filter((k): k is PersonKey => !!k && !!ref.personByKey[k]);
  switch (choice) {
    case "self":
      // About more than one kind of person: the student (as the backend).
      return known([facets.roles.length > 1 ? ctx.student : ctx.about]);
    case "parents":
      return known(ctx.parents);
    case "tutors":
      return known(tutorsInContext(ref, ctx, assign.tutorSubject));
    case "event_person":
      return known([ctx.eventPerson]);
    case "task_assignees":
      return known(ctx.task?.assignees ?? []);
    case "admins":
      return ref.admins;
    case "all_tutors":
      return ref.tutors.filter(
        (t) =>
          assign.teachSubject === null ||
          ref.subjectsOfTutor[t]?.includes(assign.teachSubject)
      );
  }
}

/**
 * An example event for a trigger, from the organization's own people: a
 * student with tutors and parents, a parent with a student, a tutor, or one
 * of Tadpole's tasks. null when the organization has nobody to show.
 */
export function sampleContext(
  ref: ReferenceData,
  trigger: Trigger
): { label: string; ctx: RunContext } | null {
  if (isTaskKind(trigger.kind)) {
    const task =
      ref.tasks.find(
        (t) =>
          t.status === TaskStatus.PENDING &&
          t.assignees.length &&
          t.about.length
      ) ??
      ref.tasks.find((t) => t.assignees.length) ??
      null;
    if (!task) return null;
    const about = task.about[0] ?? null;
    return {
      label: task.name,
      ctx: {
        ...emptyContext(),
        about,
        ...familyOf(ref, about),
        eventPerson: task.assignees[0] ?? ref.me,
        task: {
          id: task.id,
          name: task.name,
          status: task.status,
          assignees: task.assignees,
          about: task.about,
        },
      },
    };
  }
  // A families form is most often filled out by a parent.
  const roles = aboutRoles(ref, trigger);
  const role =
    trigger.options?.form_id != null && roles.includes("parent")
      ? "parent"
      : roles[0];
  let about: PersonKey | null = null;
  if (role === "student")
    about =
      ref.students.find(
        (s) => ref.tutorsOf[s]?.length && ref.parentsOf[s]?.length
      ) ??
      ref.students.find((s) => ref.tutorsOf[s]?.length) ??
      ref.students[0] ??
      null;
  else if (role === "parent")
    about =
      ref.parents.find((p) => ref.studentsOfParent[p]?.length) ??
      ref.parents[0] ??
      null;
  else if (role === "tutor") about = ref.tutors[0] ?? null;
  if (!about) return null;
  const formId =
    trigger.options?.form_id != null ? String(trigger.options.form_id) : null;
  const filler = trigger.kind === "form_filled" ? about : null;
  return {
    label: filler
      ? `${nameOf(ref, about)} filling out ${formName(ref, formId)}`
      : nameOf(ref, about),
    ctx: {
      ...emptyContext(),
      about,
      ...familyOf(ref, about),
      eventPerson: filler,
      formId,
    },
  };
}
